import { ApiUser } from 'api/userEnvironment/types/ApiUserEnvironment';

import { NEW_YORK_CITY_COORDINATES } from './constants/geo';
import { isLocationWithinRadius } from './geo/isLocationWithinRadius';
import { isAdmin, isLocationInCountry, isNonAdmin } from './userEnvironment';

export function promoteResumeWebinar({ user }: { user: ApiUser | null }) {
  if (!user) return false;

  const nonAdmin = isNonAdmin(user);

  const usEnOnly = Boolean(
    isLocationInCountry(user, 'US') && user.locale === 'en',
  );

  const afterStartDate = new Date() > new Date('2024-07-02T00:00:00.000-04:00');

  const beforeEndDate = new Date() < new Date('2024-07-23T23:59:00.000-04:00');

  return nonAdmin && usEnOnly && afterStartDate && beforeEndDate;
}

export function promoteOfficeHours({ user }: { user: ApiUser | null }) {
  if (!user) return false;

  const nonAdmin = isNonAdmin(user);

  const usEnOnly = Boolean(
    isLocationInCountry(user, 'US') && user.locale === 'en',
  );

  const userCreated = new Date(user.created);
  const withinSixMonths =
    userCreated > new Date('2023-10-20') &&
    userCreated < new Date('2024-04-20');

  const beforeCutoffDate =
    new Date() < new Date('2024-04-24T12:00:00.000-04:00');

  return nonAdmin && usEnOnly && withinSixMonths && beforeCutoffDate;
}

export function promoteOfficeHoursApril24({ user }: { user: ApiUser | null }) {
  if (!user) return false;

  const nonAdmin = isNonAdmin(user);

  const usEnOnly = Boolean(
    isLocationInCountry(user, 'US') && user.locale === 'en',
  );

  const createdAfterCutoff = new Date(user.created) > new Date('2024-04-03');

  const afterStartDate = new Date() > new Date('2024-06-20T00:00:00.000-04:00');

  const beforeEndDate = new Date() < new Date('2024-07-11T23:59:00.000-04:00');

  return (
    nonAdmin &&
    usEnOnly &&
    createdAfterCutoff &&
    afterStartDate &&
    beforeEndDate
  );
}

export function promoteWebinarAugust2024({ user }: { user: ApiUser | null }) {
  if (!user) return false;

  const nonAdmin = isNonAdmin(user);

  const usEnOnly = Boolean(
    isLocationInCountry(user, 'US') && user.locale === 'en',
  );

  const afterStartDate = new Date() > new Date('2024-07-25T00:00:00.000-04:00');

  const beforeEndDate = new Date() < new Date('2024-08-15T10:00:00.000-04:00');

  return nonAdmin && usEnOnly && afterStartDate && beforeEndDate;
}

export function promoteEsSurvey({ user }: { user: ApiUser | null }) {
  if (!user) return false;

  const nonAdmin = Boolean(isNonAdmin(user));

  const inCountry = [
    'AR',
    'BO',
    'CL',
    'CO',
    'CR',
    'CU',
    'DO',
    'EC',
    'ES',
    'GQ',
    'GT',
    'HN',
    'MX',
    'NI',
    'PA',
    'PE',
    'PR',
    'PY',
    'SV',
    'UY',
    'VE',
  ].some((country) => isLocationInCountry(user, country));

  const esInCountryOnly = Boolean(user.locale === 'es' && inCountry);

  const afterStartDate = new Date() > new Date('2024-07-02T00:00:00.000-04:00');

  const beforeEndDate = new Date() < new Date('2024-07-23T23:59:00.000-04:00');

  return nonAdmin && esInCountryOnly && afterStartDate && beforeEndDate;
}

export function promoteGradfair2024(
  user: ApiUser | null,
  startDate: Date,
  endDate: Date,
) {
  if (!user) return false;

  const nonAdmin = Boolean(isNonAdmin(user));

  const usEnOnly = Boolean(
    isLocationInCountry(user, 'US') && user.locale === 'en',
  );

  const withinRadius = isLocationWithinRadius(
    {
      latitude: user.location.latitude,
      longitude: user.location.longitude,
    },
    NEW_YORK_CITY_COORDINATES,
    50,
  );

  const afterStartDate = new Date() > startDate;

  const beforeEndDate = new Date() < endDate;

  return (
    nonAdmin && usEnOnly && withinRadius && afterStartDate && beforeEndDate
  );
}

export function promoteAdminSurvey2024(user: ApiUser | null) {
  if (!user) return false;

  const isAdminUser = isAdmin(user);

  const usEnOnly = Boolean(
    isLocationInCountry(user, 'US') && user.locale === 'en',
  );

  const afterStartDate = new Date() > new Date('2024-08-11T00:00:00.000-04:00');

  const beforeEndDate = new Date() < new Date('2024-09-13T00:00:00.000-04:00');

  return isAdminUser && usEnOnly && afterStartDate && beforeEndDate;
}
